import { RouteComponentProps } from '@reach/router';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { alpha, styled } from '@mui/material/styles';
import { useNavigate } from '@reach/router';
import { useDispatch } from 'react-redux';
import { setAxisStructuredData } from '../../store/axisStore';
import {
  AXIS_INQUIRY_OPTIONS,
  AxisTopLevelStructuredData,
} from './axisStructuredDataConstants';
import { AxisLogo } from './components/AxisLogo';

interface AxisWelcomePageProps extends RouteComponentProps {}

const formatAxisOptionLabel = (label: string) => label.replace(' / ', '/');

const Outer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: `calc(100vh - ${theme.spacing(14)})`,
  padding: `clamp(${theme.spacing(2.5)}, 4vw, ${theme.spacing(4.75)})`,
  fontFamily: theme.typography.fontFamily,
  [theme.breakpoints.down('sm')]: {
    minHeight: 'auto',
    padding: theme.spacing(2.5, 1.5),
  },
}));

const WelcomeCard = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: `min(100%, clamp(${theme.spacing(102.75)}, 82vw, ${theme.spacing(
    122.5,
  )}))`,
  minHeight: `clamp(${theme.spacing(53)}, 44vw, ${theme.spacing(65)})`,
  padding: `clamp(${theme.spacing(4.875)}, 4vw, ${theme.spacing(
    6.5,
  )}) clamp(${theme.spacing(2.25)}, 4vw, ${theme.spacing(
    5,
  )}) clamp(${theme.spacing(6.5)}, 5vw, ${theme.spacing(8.5)})`,
  border: `${theme.spacing(0.375)} solid ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  boxShadow: `0 ${theme.spacing(1.75)} ${theme.spacing(4)} ${alpha(
    theme.palette.primary.dark,
    0.1,
  )}`,
  textAlign: 'center',
  fontFamily: theme.typography.fontFamily,
  [theme.breakpoints.down('sm')]: {
    minHeight: 'auto',
    padding: theme.spacing(3, 2, 4),
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h5,
  color: theme.palette.primary.main,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.fontWeightBold,
  lineHeight: 1.2,
  marginBottom: theme.spacing(1.1),
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle1,
  color: theme.palette.text.secondary,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: 1.4,
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(3),
  },
}));

const PrimaryButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  marginBottom: theme.spacing(3.9),
}));

const SecondaryButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(1.5, 1.25),
  width: theme.spacing(69.25),
  maxWidth: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  ...theme.typography.button,
  width: theme.spacing(22.25),
  minWidth: theme.spacing(22.25),
  height: theme.spacing(4.25),
  padding: theme.spacing(0.75, 2.5),
  borderRadius: theme.spacing(2.25),
  textTransform: 'none',
  fontFamily: theme.typography.fontFamily,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    width: theme.spacing(30),
    minWidth: theme.spacing(30),
  },
}));

const PrimaryActionButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  boxShadow: `0 ${theme.spacing(0.75)} ${theme.spacing(1.75)} ${alpha(
    theme.palette.primary.main,
    0.28,
  )}`,

  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: `0 ${theme.spacing(1)} ${theme.spacing(2.25)} ${alpha(
      theme.palette.primary.main,
      0.34,
    )}`,
  },
}));

const SecondaryActionButton = styled(ActionButton)(({ theme }) => ({
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.background.paper,
  boxShadow: `0 ${theme.spacing(0.5)} ${theme.spacing(1.25)} ${alpha(
    theme.palette.primary.dark,
    0.1,
  )}`,

  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 ${theme.spacing(0.625)} ${theme.spacing(1.5)} ${alpha(
      theme.palette.primary.dark,
      0.14,
    )}`,
  },
}));

export const AxisWelcomePage = (_props: AxisWelcomePageProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [primaryOption, ...secondaryOptions] = AXIS_INQUIRY_OPTIONS;

  const handleNavigate = (
    url: string,
    structuredData: AxisTopLevelStructuredData,
  ) => {
    dispatch(
      setAxisStructuredData({
        ...structuredData,
        industry: '',
        subIndustry: '',
      }),
    );
    navigate(url);
  };

  return (
    <Outer>
      <WelcomeCard elevation={0}>
        <AxisLogo />

        <Title variant='h5' variantMapping={{ h5: 'h1' }}>
          Contact Axis
        </Title>

        <Subtitle variant='subtitle1'>
          Please select an option below to continue
        </Subtitle>

        <PrimaryButtonContainer>
          <PrimaryActionButton
            variant='contained'
            onClick={() =>
              handleNavigate(primaryOption.url, primaryOption.structuredData)
            }
          >
            {primaryOption.label}
          </PrimaryActionButton>
        </PrimaryButtonContainer>

        <SecondaryButtonsContainer>
          {secondaryOptions.map(({ label, url, structuredData }) => (
            <SecondaryActionButton
              key={label}
              variant='outlined'
              onClick={() => handleNavigate(url, structuredData)}
            >
              {formatAxisOptionLabel(label)}
            </SecondaryActionButton>
          ))}
        </SecondaryButtonsContainer>
      </WelcomeCard>
    </Outer>
  );
};
